import { Link } from "react-router-dom";
import { ArrowUpRight, ScanFace, Swords, Trophy, Zap, Mountain, Target, TrendingUp, MessageCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";

const DISCORD_URL = "https://discord.gg/looksmaxing";

const STATS = [
  { value: "0-100", label: "Mog Score" },
  { value: "6", label: "Sub-metrics" },
  { value: "1v1", label: "Battle Mode" },
  { value: "7+", label: "Ascending Guides" },
];

const FEATURES = [
  {
    icon: ScanFace,
    title: "AI Facial Diagnostic",
    body: "Upload a selfie. Get a brutally honest looksmaxxing breakdown — jawline, skin, eyes, harmony, symmetry, hairline — scored, ranked, and tier-classified.",
  },
  {
    icon: Swords,
    title: "1v1 Mog Battle Arena",
    body: "Step into the ring. Vote head-to-head on real users. Win, climb the ELO ladder, and prove you're not the one being mogged.",
  },
  {
    icon: Target,
    title: "Surgical Improvement Plan",
    body: "No generic advice. Every scan generates a personalized action stack ranked by impact — the highest-leverage moves come first.",
  },
  {
    icon: TrendingUp,
    title: "Track The Ascension",
    body: "Monthly re-scans, ELO history, battle record. See your glow-up in numbers — because feelings lie, data doesn't.",
  },
];

export default function Landing() {
  const { user } = useAuth();
  const ctaHref = user ? "/dashboard" : "/register";
  const ctaLabel = user ? "Enter Dashboard" : "Start Ascending";

  return (
    <div className="relative z-10 min-h-screen bg-[var(--bg)] text-[var(--text)]">
      {/* Top bar */}
      <header className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Mountain className="w-5 h-5 text-[var(--primary)]" strokeWidth={2.5} />
            <span className="font-display text-2xl tracking-wider">ASCEND</span>
          </div>
          <nav className="flex items-center gap-3">
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="landing-discord-top"
              className="hidden sm:inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-[var(--primary)] hover:text-black px-3 py-2 border border-[var(--primary-dim)] hover:bg-[var(--primary)] transition-all"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Discord
            </a>
            <Link to="/login" className="text-xs font-mono uppercase tracking-widest text-[var(--text-muted)] hover:text-white px-3 py-2" data-testid="landing-login-link">
              Sign in
            </Link>
            <Link to={ctaHref} className="btn-brutal text-xs" data-testid="landing-cta-top">
              {ctaLabel} →
            </Link>
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-[var(--border)] relative overflow-hidden">
        <div className="absolute inset-0 dotted-grid opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 py-16 md:py-24 grid grid-cols-12 gap-8 relative">
          <div className="col-span-12 lg:col-span-7">
            <div className="flex items-center gap-3 mb-8">
              <span className="tape-label">LOOKSMAXX PROTOCOL</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">
                v1.0 // EST. 2026
              </span>
            </div>
            <h1 className="font-display text-[14vw] md:text-[9vw] lg:text-[7.5vw] leading-[0.85] tracking-tight">
              STOP BEING<br />
              <span className="text-[var(--primary)]">MOGGED.</span><br />
              START<br />
              ASCENDING.
            </h1>
            <p className="mt-8 max-w-xl text-base md:text-lg text-[var(--text-muted)] leading-relaxed">
              Ascend is the world's most advanced AI-powered looksmaxxing platform.
              Upload a scan. Get rated 0–100. Receive a surgical improvement plan.
              Then prove it in the Mog Battle arena.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to={ctaHref} className="btn-brutal" data-testid="landing-cta-hero">
                {ctaLabel} <ArrowUpRight className="inline w-4 h-4 ml-1" />
              </Link>
              <Link to="/login" className="btn-ghost" data-testid="landing-signin-hero">
                I already ascended
              </Link>
              <a
                href={DISCORD_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="landing-discord-hero"
                className="btn-ghost inline-flex items-center gap-2"
              >
                <MessageCircle className="w-4 h-4" /> Join Discord
              </a>
            </div>
            <div className="mt-12 grid grid-cols-4 gap-4 max-w-xl">
              {STATS.map((s) => (
                <div key={s.label} className="border-l-2 border-[var(--primary)] pl-3">
                  <div className="font-display text-3xl">{s.value}</div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-5 relative">
            <div className="panel-bright relative aspect-[4/5] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/29433729/pexels-photo-29433729.png?auto=compress&cs=tinysrgb&dpr=2&h=900&w=720"
                alt="Looksmaxxing diagnostic"
                className="w-full h-full object-cover grayscale contrast-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              {/* Overlay HUD */}
              <div className="absolute top-4 left-4 right-4 flex justify-between text-[10px] font-mono uppercase tracking-widest text-white/80">
                <span>SUBJECT // 042</span>
                <span className="text-[var(--primary)]">● LIVE SCAN</span>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <div className="flex items-end justify-between mb-2">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-white/60">OVERALL MOG SCORE</span>
                  <span className="text-[10px] font-mono text-[var(--primary)]">TIER: CHAD</span>
                </div>
                <div className="flex items-baseline gap-3">
                  <span className="font-display text-7xl text-white pulse-score">87</span>
                  <span className="font-mono text-sm text-white/60">/100</span>
                </div>
                <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] font-mono">
                  {[["JAW", 92], ["SKIN", 81], ["HARMONY", 88]].map(([k, v]) => (
                    <div key={k} className="border border-white/20 px-2 py-1">
                      <div className="text-white/50">{k}</div>
                      <div className="text-white">{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="absolute -bottom-4 -left-4 panel px-4 py-2 hidden md:block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">Analyzed by</div>
              <div className="text-sm font-mono text-white">Ascend Vision Engine</div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee */}
      <section className="border-b border-[var(--border)] py-4 overflow-hidden bg-[var(--surface)]">
        <div className="marquee-track">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-12 px-12 whitespace-nowrap font-display text-2xl tracking-widest text-[var(--text-muted)]">
              <span>MEWING</span><span className="text-[var(--primary)]">+</span>
              <span>JAWLINE</span><span className="text-[var(--primary)]">+</span>
              <span>SKINMAXX</span><span className="text-[var(--primary)]">+</span>
              <span>HAIRMAXX</span><span className="text-[var(--primary)]">+</span>
              <span>FRAME</span><span className="text-[var(--primary)]">+</span>
              <span>STYLE</span><span className="text-[var(--primary)]">+</span>
              <span>POSTURE</span><span className="text-[var(--primary)]">+</span>
              <span>HARMONY</span><span className="text-[var(--primary)]">+</span>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20">
          <div className="grid grid-cols-12 gap-8 mb-12">
            <div className="col-span-12 md:col-span-4">
              <span className="tape-label">PROTOCOL</span>
              <h2 className="font-display text-5xl md:text-6xl mt-4 leading-none">
                FOUR PILLARS<br />OF ASCENDING.
              </h2>
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6 self-end">
              <p className="text-[var(--text-muted)] text-lg">
                No fluff. No copium. Just the systems that move the needle from sub-5 to legendary.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-px bg-[var(--border)]">
            {FEATURES.map(({ icon: Icon, title, body }, i) => (
              <div key={title} className="col-span-12 md:col-span-6 lg:col-span-3 bg-[var(--bg)] p-8 hover:bg-[var(--surface)] transition-colors group">
                <div className="flex items-start justify-between mb-6">
                  <Icon className="w-7 h-7 text-[var(--primary)]" strokeWidth={2} />
                  <span className="font-mono text-xs text-[var(--text-muted)]">0{i + 1}</span>
                </div>
                <h3 className="font-display text-2xl mb-3 tracking-wide group-hover:text-[var(--primary)] transition-colors">
                  {title}
                </h3>
                <p className="text-sm text-[var(--text-muted)] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-20 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <span className="tape-label">THE METHOD</span>
            <h2 className="font-display text-5xl md:text-6xl mt-4 leading-none">
              FROM SCAN<br />TO ASCENSION.
            </h2>
            <p className="text-[var(--text-muted)] mt-6 max-w-md">
              A four-step protocol. Repeat monthly. The mirror keeps the receipts.
            </p>
          </div>
          <div className="col-span-12 md:col-span-7 space-y-px bg-[var(--border)]">
            {[
              { n: "01", t: "Upload Your Scan", d: "Front-facing portrait, good lighting, neutral expression. Takes 10 seconds." },
              { n: "02", t: "Receive AI Diagnostic", d: "Vision engine returns your overall mog score, six sub-scores, and a personalized improvement queue." },
              { n: "03", t: "Execute The Stack", d: "Follow your ranked action plan — highest-impact moves first. Mewing, skincare, lifts, style." },
              { n: "04", t: "Battle. Re-scan. Climb.", d: "Test your gains in the Mog Battle arena. Re-scan monthly. Ascend the leaderboard." },
            ].map((s) => (
              <div key={s.n} className="bg-[var(--bg)] p-6 flex gap-6 items-start hover:bg-[var(--surface)] transition-colors">
                <span className="font-display text-5xl text-[var(--primary)] leading-none">{s.n}</span>
                <div>
                  <h3 className="font-display text-2xl mb-1 tracking-wide">{s.t}</h3>
                  <p className="text-sm text-[var(--text-muted)]">{s.d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-b border-[var(--border)] relative">
        <div className="absolute inset-0 stripes opacity-40" />
        <div className="max-w-[1400px] mx-auto px-6 py-24 text-center relative">
          <Zap className="w-10 h-10 text-[var(--primary)] mx-auto mb-6" />
          <h2 className="font-display text-6xl md:text-8xl leading-none">
            THE LADDER<br />
            <span className="text-[var(--primary)]">IS OPEN.</span>
          </h2>
          <p className="text-[var(--text-muted)] mt-6 max-w-lg mx-auto">
            Free forever. No fluff. No paywalls. Just the most honest mirror you'll ever look into.
          </p>
          <div className="mt-10 flex justify-center gap-4 flex-wrap">
            <Link to={ctaHref} className="btn-brutal" data-testid="landing-cta-bottom">
              {ctaLabel} <ArrowUpRight className="inline w-4 h-4 ml-1" />
            </Link>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="landing-discord-bottom"
              className="btn-ghost inline-flex items-center gap-2 border-[var(--primary)] text-[var(--primary)] hover:bg-[var(--primary)] hover:text-black"
            >
              <MessageCircle className="w-4 h-4" /> Join the Discord
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
